import React, { useRef, useState } from 'react';
import { forgotPassword, resetPassword, verifyResetPasswordOTP } from 'data';
import { useNavigate } from 'react-router-dom';
import { Button, InputField, LanguageSwitcher } from 'components';
import { BlueLogo } from 'icons';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import { InputOtp } from 'primereact/inputotp';

export const ResetPassword: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const toast = useRef<Toast>(null);
	const [otp, setOtp] = useState('');
	const [otpCorrect, setOtpCorrect] = useState(false);
	const [formData, setFormData] = useState({
		password: '',
		confirmPassword: '',
	});

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleOTPSubmit = async () => {
		try {
			await verifyResetPasswordOTP(otp);
			setOtpCorrect(true);
		} catch (error) {
			setOtp('');
			if (toast.current) {
				toast.current.show({
					severity: 'error',
					summary: 'Error',
					detail: t('resetPassword.errorOtpToast'),
					life: 4000,
				});
			}
		}
	};
	
	const handleResetPasswordSubmit = async () => {
		const { password, confirmPassword } = formData;
		if (password.length < 8) {
			if (toast.current) {
				toast.current.show({
					severity: 'error',
					summary: 'Error',
					detail: t('resetPassword.passwordLengthToast'),
					life: 5000,
				});
			}
			return;
		}
		if (password !== confirmPassword) {
			if (toast.current) {
				toast.current.show({
					severity: 'error',
					summary: 'Error',
					detail: t('resetPassword.passwordMismatchToast'),
					life: 5000,
				});
			}
			return;
		}

		const req = {
			otp,
			newPassword: password,
			confirmNewPassword: confirmPassword
		}
		try {
			await resetPassword(req);
			setOtpCorrect(true);
			if (toast.current) {
				toast.current.show({
					severity: 'success',
					summary: 'Success',
					detail: t('resetPassword.successToast'),
					life: 4000,
				})
			}
			setFormData({ password: '', confirmPassword: '' });
			setTimeout(() => {
				navigate('/login');
			}, 2000);
		} catch (error) {
			setOtp('');
			setFormData({ password: '', confirmPassword: '' });
			if (toast.current) {
				toast.current.show({
					severity: 'error',
					summary: 'Error',
					detail: t('resetPassword.errorResetPasswordToast'),
					life: 4000,
				});
			}
		}
	};

	return (
		<div
			className="relative flex items-center justify-center min-h-svh bg-[#F5F7F9] flex-col space-y-[24px] px-[16px] py-[32px] sm:py-[64px]"
			tabIndex={0}>
			<Toast ref={toast} position="top-right" />
			<LanguageSwitcher className="absolute top-[16px] right-[16px] sm:top-[24px] sm:right-[24px]" />
			<div>
				<BlueLogo width={122} height={33} />
			</div>
			<div className="bg-white border-[#D5D9DF] border p-[24px] sm:p-[32px] rounded-[24px] pb-[36px] w-full text-black flex flex-col items-center max-w-[440px] sm:max-w-[500px] space-y-[17px]">
				<div className="flex flex-col space-y-[4px]">
					<h2 className="text-[24px] leading-[29px] font-[500] text-center text-[#2E435F]">
						{t('resetPassword.title')}
					</h2>
					<h2 className="text-[14px] font-[400] text-center text-[#58697F]">
						{t('resetPassword.subtitle')}
					</h2>
				</div>
				{!otpCorrect && (
					<div className="flex flex-col space-y-[4px]">
						<div className="space-y-[12px] w-full">
							<InputOtp value={otp} onChange={(e: any) => setOtp(e.value)} integerOnly/>
						</div>
						<Button
							label={t('resetPassword.verifyOtp')}
							onClick={handleOTPSubmit}
							style={{ width: '100%' }}
						/>
					</div>
				)}
				{otpCorrect && (
					<div className="flex flex-col space-y-[4px] w-[75%]">
						<InputField
							label={t('register.password')}
							type="password"
							name="password"
							placeholder={t('register.password')}
							value={formData.password}
							onChange={handleChange}
						/>
						<InputField
							label={t('register.confirmPassword')}
							type="password"
							name="confirmPassword"
							placeholder={t('register.confirmPassword')}
							value={formData.confirmPassword}
							onChange={handleChange}
						/>
						<Button
							label={t('resetPassword.resetPassword')}
							onClick={handleResetPasswordSubmit}
							style={{ width: '100%', marginTop: '16px' }}
						/>
					</div>
				)}
			</div>
			<p className="mt-4 text-sm text-[#58697F] font-[700]">
				<span
				className="text-[#FF6F61] cursor-pointer hover:underline"
				onClick={() => navigate('/forgot-password')}>
					{t('forgotPassword.goBack')}
				</span>
			</p>
		</div>
	);
};
